import {DrawerModal} from "@zendeskgarden/react-modals";
import * as css from "./styles/renovations-drawer.module.scss";
import {ReactElement, useEffect, useRef, useState} from "react";
import {Button, IconButton} from "@zendeskgarden/react-buttons";
import {useGetRenovationsPackagesByPropertyIdAndUnitTypeId} from "./data/UseGetRenovationsPackagesByPropertyIdAndUnitTypeId";
import {GetRenovationCostCategoriesQuery, useCreateRenovationPackageMutation, useDeleteRenovationPackageMutation, useUpdateRenovationPackageMutation} from "../../__generated__/generated_types";
import BarLoader from "../bar-loader/BarLoader";
import HotTable from "@handsontable/react";
import {LICENSES} from "../../constants/Licenses";
import "../../components/vz-ui/vz-hot-table/styles/vz-hot-table.scss";
import Handsontable from "handsontable";
import {addPackage, buildColumnConfig, packageRemovalButtonMarkup, refreshCellsConfig, removePackage, getChanges, summarizeAllColTotals} from "./helpers/renovation-drawer-utils";
import {toast} from "react-toastify";
import {Field, MediaInput} from "@zendeskgarden/react-forms";
import { ReactComponent as SearchIcon } from '@zendeskgarden/svg-icons/src/16/search-stroke.svg';
import {Add, Close} from "@material-ui/icons";


interface AnalystRenovationDrawerProps {
    show: boolean;
    handleClose: (triggerRefetch?: boolean) => void;
    propertyId: string;
    unitTypeId: string;
    unitTypeName: string;
    budgetYear: number;
    renovationCostCategories: GetRenovationCostCategoriesQuery | undefined;
}

export interface RenovationsPackageTableRowPackage {
    id: string;
    name: string;
    amount: string | number | null;
    delay: string | number | null;
    originalName: string;
    originalAmount: string | number | null;
    originalDelay: string | number | null;
}

export interface RenovationsPackageTableRow {
    categoryId: string;
    categoryName: string;
    categoryGlNumber: string;
    packages: RenovationsPackageTableRowPackage[];
}

const userEntryInputSources = ["edit", "CopyPaste.paste", "UndoRedo.undo", "UndoRedo.redo", "Autofill.fill"];

export default function AnalystRenovationDrawer(props: AnalystRenovationDrawerProps): ReactElement {
    const hotRef = useRef<HotTable>(null);
    const newPackageCounter = useRef<number>(1);
    const existingPackageIds = useRef<Set<string>>(new Set());
    const packageNameIdMap = useRef<Map<string, string>>(new Map());
    const changesSaved = useRef(false);

    const [isLoading, setIsLoading] = useState(false);
    const [tableData, setTableData] = useState<(RenovationsPackageTableRow)[]>([]);
    const [filterText, setFilterText] = useState("");

    const {
        isDataReady: isRenovationPackagesDataReady,
        refreshRenovationPackagesForSelectedProperty,
        getData: renovationPackageData
    } = useGetRenovationsPackagesByPropertyIdAndUnitTypeId();

    const [deleteRenovationPackage] = useDeleteRenovationPackageMutation();
    const [updateRenovationPackage] = useUpdateRenovationPackageMutation();
    const [createRenovationPackage] = useCreateRenovationPackageMutation();

    async function saveChanges() {
        setIsLoading(true);

        async function promisesSettled<T>(promises: Promise<T>[]): Promise<boolean> {
            const results = await Promise.allSettled(promises);
            return results.every(result => result.status === "fulfilled");
        }

        try {
            const {packagesToCreate, packagesToUpdate, packagesToDelete} = getChanges(tableData, existingPackageIds.current, packageNameIdMap.current);

            const deleteSuccess = await promisesSettled(
                Array.from(packagesToDelete.values()).map(renoPackageId =>
                    deleteRenovationPackage({
                        variables: {
                            id: renoPackageId,
                            force: true,
                        },
                    })
                )
            );

            if (!deleteSuccess) {
                toast.error("There was a problem deleting packages, please try again");
                return;
            }

            const updateSuccess = await promisesSettled(
                Array.from(packagesToUpdate.values()).map(renoPackage =>
                    updateRenovationPackage({
                        variables: {
                            id: renoPackage.id,
                            name: renoPackage.name,
                            premium: renoPackage.premium == null ? undefined : renoPackage.premium,
                            premiumDelayRecognition: renoPackage.premiumDelayRecognition == null ? undefined : +renoPackage.premiumDelayRecognition,
                            costCategoryMappings: renoPackage.costCategoryMappings.map(mapping => ({
                                costCategoryId: mapping.costCategoryId,
                                amount: mapping.amount,
                                delayRecognition: mapping.delayRecognition == null ? undefined : +mapping.delayRecognition,
                            })),
                        },
                    })
                )
            );

            if (!updateSuccess) {
                toast.error("There was a problem updating packages, please try again");
                return;
            }

            const createSuccess = await promisesSettled(
                Array.from(packagesToCreate.values()).map(renoPackage =>
                    createRenovationPackage({
                        variables: {
                            name: renoPackage.name,
                            propertyId: props.propertyId,
                            unitTypeId: props.unitTypeId,
                            premium: renoPackage.premium == null ? undefined : renoPackage.premium,
                            premiumDelayRecognition: renoPackage.premiumDelayRecognition == null ? undefined : +renoPackage.premiumDelayRecognition,
                            costCategoryMappings: renoPackage.costCategoryMappings.map(mapping => ({
                                costCategoryId: mapping.costCategoryId,
                                amount: mapping.amount,
                                delayRecognition: mapping.delayRecognition == null ? undefined : +mapping.delayRecognition,
                            })),
                        },
                    })
                )
            );

            if (!createSuccess) {
                toast.error("There was a problem creating packages, please try again");
                return;
            }

            if ((packagesToCreate.size + packagesToUpdate.size + packagesToDelete.size) > 0) {
                changesSaved.current = true;
            }

            toast.success("Packages saved");
        } catch {
            toast.error("There was a problem saving packages, please try again");
        } finally {
            refreshRenovationPackagesForSelectedProperty(props.propertyId, props.unitTypeId, props.budgetYear);
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (props.propertyId && props.unitTypeId) {
            refreshRenovationPackagesForSelectedProperty(props.propertyId, props.unitTypeId, props.budgetYear);
        }
    }, [props.propertyId, props.unitTypeId]);

    useEffect(() => {
        if (props.show === true) {
            changesSaved.current = false;
        }
    }, [props.show]);

    useEffect(() => {
        if (isRenovationPackagesDataReady && props.renovationCostCategories) {
            existingPackageIds.current.clear();
            packageNameIdMap.current.clear();

            const data: RenovationsPackageTableRow[] = [];
            const sortedPackageData = renovationPackageData().sortBy("createdAt");

            const firstRow = {
                categoryId: "col-header-row",
                categoryName: "",
                categoryGlNumber: "Renovation Package",
                packages: sortedPackageData.map(renoPackage => ({
                    id: renoPackage.id,
                    name: renoPackage.name,
                    amount: renoPackage.name,
                    delay: packageRemovalButtonMarkup,
                    originalAmount: null,
                    originalDelay: null,
                    originalName: renoPackage.name,
                }))
            };

            const secondRow = {
                categoryId: "col-header-row",
                categoryName: "Cost Category",
                categoryGlNumber: "GL Number",
                packages: sortedPackageData.map(renoPackage => ({
                    id: renoPackage.id,
                    name: renoPackage.name,
                    amount: "Amount",
                    delay: "Delay Recognition",
                    originalAmount: null,
                    originalDelay: null,
                    originalName: renoPackage.name,
                }))
            };

            data.push(firstRow, secondRow);

            for (const category of props.renovationCostCategories.queryRenovationCostCategories) {
                const row: RenovationsPackageTableRow = {
                    categoryId: category.id,
                    categoryName: category.name,
                    categoryGlNumber: category.account.glName,
                    packages: [],
                };

                for (const renoPackage of sortedPackageData) {
                    const costCategoryValues = renoPackage.costCategoryMappings.find(x => x.renovationCostCategoryId == category.id);
                    existingPackageIds.current.add(renoPackage.id);
                    packageNameIdMap.current.set(renoPackage.id, renoPackage.name);

                    row.packages.push({
                        id: renoPackage.id,
                        name: renoPackage.name,
                        amount: costCategoryValues?.amount ?? null,
                        delay: costCategoryValues?.delayRecognition ?? null,
                        originalAmount: costCategoryValues?.amount ?? null,
                        originalDelay: costCategoryValues?.delayRecognition ?? null,
                        originalName: renoPackage.name,
                    });
                }

                data.push(row);
            }

            const premiumRow = {
                categoryId: "premium",
                categoryName: "",
                categoryGlNumber: "Package Premium",
                packages: sortedPackageData.map(renoPackage => ({
                    id: renoPackage.id,
                    name: renoPackage.name,
                    amount: renoPackage.premium,
                    delay: renoPackage.premiumDelayRecognition,
                    originalAmount: renoPackage.premium,
                    originalDelay: renoPackage.premiumDelayRecognition,
                    originalName: renoPackage.name,
                }))
            };

            const totalsRow = {
                categoryId: "totals",
                categoryName: "",
                categoryGlNumber: "Cost Category Total",
                packages: sortedPackageData.map(() => ({
                    id: "",
                    name: "",
                    amount: 0,
                    delay: null,
                    originalAmount: 0,
                    originalDelay: null,
                    originalName: "",
                }))
            };

            data.push(totalsRow, premiumRow);

            setTableData(data);
            setIsLoading(false);
        } else {
            setIsLoading(true);
        }
    }, [isRenovationPackagesDataReady, props.renovationCostCategories, renovationPackageData(), props.show]);

    // when data changes, make sure the correct styling and configuration is applied to cells
    useEffect(() => {
        const hot = hotRef.current?.hotInstance;

        if (hot && tableData.length > 0) {
            const isFiltered = filterText != null && filterText.trim() != "";
            refreshCellsConfig(isFiltered, hot);
        }
    }, [tableData]);

    useEffect(() => {
        const hot = hotRef.current?.hotInstance;

        if (!hot) {
            return;
        }

        if (filterText.trim() == "") {
            hot.updateSettings({hiddenRows: {rows: []}});
        } else {
            const rowsToHide = [];
            const lowercaseFilter = filterText.toLowerCase();

            for (const [index, data] of Array.from(tableData.entries())) {
                const shouldHideRow = data.categoryId != "col-header-row" &&
                                     !data.categoryName.toLowerCase().includes(lowercaseFilter) &&
                                     !data.categoryGlNumber.toLowerCase().includes(lowercaseFilter);

                if (shouldHideRow) {
                    rowsToHide.push(index);
                }
            }

            hot.updateSettings({hiddenRows: {rows: rowsToHide}});
        }
    }, [filterText]);

    return (
        <DrawerModal
            isOpen={props.show}
            onClose={() => {
                props.handleClose(changesSaved.current);
            }}
            className={css.renoDrawer}
        >
            <DrawerModal.Header>
                Renovation Package Management
            </DrawerModal.Header>

            <DrawerModal.Body className={css.bodyWrapper}>
                <BarLoader loading={isLoading} />

                <div className={css.bodyTitleWrapper}>
                    <h5 className={css.unitTypeLabel}>Unit Type</h5>
                    <h4>{props.unitTypeName}</h4>
                </div>

                <div className={css.controlsWrapper}>
                    <Field className={css.filterInput}>
                        <MediaInput
                            start={<SearchIcon />}
                            placeholder="Search cost categories"
                            value={filterText}
                            onChange={(evt) => {
                                setFilterText(evt.target.value);
                            }}
                            aria-label="search cost categories"
                            end={filterText != "" ?
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setFilterText("");
                                    }}
                                    aria-label="clear search filter"
                                >
                                        <Close />
                                </IconButton>
                                :
                                null
                            }
                        />
                    </Field>

                    <Button
                        disabled={!props.renovationCostCategories}
                        isPrimary
                        onClick={() => {
                            const updatedTableData = addPackage(tableData, newPackageCounter, packageNameIdMap);
                            setTableData([...updatedTableData]);
                        }}
                        aria-label="add new renovation package"
                    >
                        <Button.StartIcon><Add /></Button.StartIcon>
                        Add Package
                    </Button>
                </div>

                <div className={`${css.tableWrapper} vz-hot-table`}>
                    <HotTable
                        ref={hotRef}
                        data={tableData}
                        columns={buildColumnConfig(tableData)}
                        colWidths={(colIndex) => {
                            if (colIndex > 1) {
                                return 120;
                            }

                            return 200;
                        }}
                        colHeaders={false}
                        height={"100%"}
                        rowHeights={48}
                        manualColumnResize={false}
                        autoColumnSize={false}
                        licenseKey={LICENSES.HandsOnTable}
                        readOnly={true}
                        fixedRowsTop={2}
                        fixedColumnsLeft={2}
                        hiddenRows={{rows: []}}
                        fixedRowsBottom={filterText ? 0 : 2}
                        beforeChange={function(this: Handsontable, changes, source) {
                            let errorMessage: string | null = null;
                            for (const change of changes) {
                                if (change && userEntryInputSources.includes(source)) {
                                    const [row, col, _oldVal, newVal] = change;
                                    let colAsString = "";

                                    if (typeof col == "string") {
                                        colAsString = col;
                                    }

                                    const isAmountCol = colAsString.includes("amount");
                                    const isDelayCol = colAsString.includes("delay");
                                    const existingNames = Array.from(packageNameIdMap.current.values());

                                    // changes to this row are package names
                                    if (row == 0) {
                                        if (newVal == null || newVal.trim() == "") {
                                            errorMessage = "Package name cannot be blank";
                                        } else if (existingNames.includes(newVal)) {
                                            errorMessage = `Name "${newVal}" already in use`;
                                        }
                                    } else if (row > 1) {
                                        // treat null values as 0
                                        if (newVal === null) {
                                            change[3] = 0;
                                        }
                                        // amounts and delays have to be whole numbers
                                        if ((isAmountCol || isDelayCol) && (isNaN(+newVal) || !Number.isInteger(+newVal))) {
                                            errorMessage = "Values must be a whole number";
                                        } else if (isDelayCol && (+newVal > 12 || +newVal < 0)) {
                                            errorMessage = "Delay Recognition must be between 0 and 12";
                                        }
                                    }
                                }
                            }

                            if (errorMessage) {
                                toast.error(errorMessage);
                                return false;
                            }
                        }}
                        afterChange={function(this: Handsontable, changes, _source) {
                            const source = _source as string;

                            if (changes && userEntryInputSources.includes(source)) {
                                for (const [row, col, _oldVal, newVal] of changes) {
                                    const colIndex = this.propToCol(col);

                                    if (row == 0) {
                                        const packageIndex = (colIndex - 2) / 2;
                                        const packageId = tableData[0]?.packages[packageIndex]?.id;

                                        if (packageId) {
                                            packageNameIdMap.current.set(packageId, newVal);
                                        }
                                    }
                                }
                            }

                            // if a change was made and it wasn't from the summary function, call the summary function
                            if (source != "calculatedColTotal") {
                                summarizeAllColTotals(this);
                            }
                        }}
                        afterOnCellMouseUp={function(this: Handsontable, event, coords) {
                            const target = event.target as HTMLElement | null;

                            if (!target) {
                                return;
                            }

                            const removePackageButton = target.closest(`.${css.removePackageButton}`);

                            if (removePackageButton) {
                                const updatedTableData = removePackage(tableData, coords.col, packageNameIdMap);
                                setTableData([...updatedTableData]);
                            }
                        }}
                    />
                </div>
            </DrawerModal.Body>

            <DrawerModal.Footer className={css.footer}>
                <DrawerModal.FooterItem>
                    <Button isBasic onClick={() => props.handleClose(changesSaved.current)}>
                        Cancel
                    </Button>
                </DrawerModal.FooterItem>
                <DrawerModal.FooterItem>
                    <Button
                        isPrimary
                        onClick={() => saveChanges()}
                        disabled={isLoading}
                    >
                        Update
                    </Button>
                </DrawerModal.FooterItem>
            </DrawerModal.Footer>

            <DrawerModal.Close />
        </DrawerModal>
    );
}
